/**
 * 🔴 **INSIDE A PRODUCTION, THE RAIL IS WHAT YOU ARE ON IT** (D157, PNK-187).
 *
 * Until D157 the rail inside a production was filtered by the COMPANY's scopes alone (`navScopes.ts`)
 * — and failed OPEN while they were unknown — so every production item was offered to everybody who
 * could see the production at all: the accountant was shown the board, the casting director the
 * petty cash, a financier the charge lines. Nothing about what the person was ON THE PRODUCTION was
 * ever asked. Now it is: `GET /productions/:id/me` answers the caller's roles, ownership,
 * administration, engagements and the union of visibility scopes they add up to, and each production
 * tile declares what it needs of that (`TileStanding`, in the manifest).
 *
 * 🔴 **THIS FAILS CLOSED, WHERE `navScopes.ts` FAILS OPEN — AND BOTH ARE RIGHT.** An unknown company
 * scope set shows everything because hiding a door the person is entitled to leaves them no way to
 * find it, and the gate is still the guard. Inside a production the cost of the other error is higher
 * and it is visible: the full menu flashing up for a financier and then shrinking to three rows reads
 * as "you were shown the budget by mistake". So while the answer is unknown nothing of the
 * production's is drawn — the Sidebar shows the production's name and a quiet skeleton — and an
 * entry with no rule is never drawn at all.
 *
 * 🔴 **Still not authority.** Nothing here may be the reason something is allowed; the gate and every
 * handler decide again, against the database, on every request.
 */
import type { NavBarItem, TileStanding } from "@/app/types";
import type { ProductionStanding } from "@/production/productionApi";
import { moduleOfRoute, opensRoute, scopeForRoute, type HeldScopes } from "./navScopes";

/**
 * The module roots whose reads a production role reaches for ITS OWN production without the company
 * scope (D157's reach list, `services/auth/production-role-reach.ts`): the money under `/costs` and
 * the production's own screens under `/productions`. Every other root still needs the company scope as
 * well — deals, time and the people directory are not on the reach list.
 */
export const ROLE_REACHED_MODULES: ReadonlyArray<string> = ["costs", "productions"];

/** Whether the caller holds ANY standing on the production — anything beyond being engaged on it. */
export function holdsStanding(standing: ProductionStanding): boolean {
  return standing.owner || standing.administers || standing.roles.length > 0 || standing.scopes.length > 0;
}

/** Engaged on it as cast, crew or background. */
export function isEngaged(standing: ProductionStanding): boolean {
  return standing.engagedAs.length > 0;
}

/**
 * Whether ONE entry's rule is met. Any clause suffices. No rule → false: a production entry that says
 * nothing about who it is for is drawn for nobody.
 *
 * 🔴 An engaged person with no standing sees the `engagedOnly` entry and NOTHING else — the `engaged`
 * clauses on Deals and Time are for engaged people who also hold standing (a producer who is also on
 * the crew), never a way into the working documents for somebody who only works on the show.
 */
export function allowsEntry(rule: TileStanding | undefined, standing: ProductionStanding): boolean {
  if (rule === undefined) return false;
  // A switched-off feature's entry is drawn for nobody, whatever else holds (D170).
  if (rule.feature !== undefined && !standing.features.includes(rule.feature)) return false;
  const standingHeld = holdsStanding(standing);
  // Her show, opened above the episode she is on (D164a): the one engaged door, never the `engaged` clauses below.
  if (rule.engagedOnly === true) return (isEngaged(standing) || standing.engagedBeneath) && !standingHeld;
  if (!standingHeld) return false;
  if (rule.anyScope?.some((scope) => standing.scopes.includes(scope))) return true;
  if (rule.anyRole?.some((role) => standing.roles.includes(role))) return true;
  if (rule.administers === true && standing.administers) return true;
  if (rule.engaged === true && isEngaged(standing)) return true;
  return false;
}

/**
 * Whether the COMPANY's scopes still have a say on this route inside a production. Not for the roots a
 * production role reaches (above), and not for a route no scope guards (`/profile`, the person's own).
 * For the rest the company scope must be KNOWN and held — unknown hides, the opposite of the app rail.
 */
export function companyScopeAllows(route: string, held: HeldScopes): boolean {
  const module = moduleOfRoute(route);
  if (module !== null && ROLE_REACHED_MODULES.includes(module)) return true;
  if (scopeForRoute(route) === null) return true;
  return held.known && opensRoute(route, held);
}

/**
 * The production's entries this person is offered. FAILS CLOSED: an unknown standing (null) is an
 * empty rail, not a full one. Only production-scoped items come back — the app's own are
 * `navScopes.ts`'s business and are filtered as they always were.
 */
export function filterProductionNavByStanding(
  items: NavBarItem[],
  standing: ProductionStanding | null,
  held: HeldScopes = { access: [], platform: [], known: false },
): NavBarItem[] {
  if (standing === null) return [];
  return items.filter(
    (item) => item.scope === "production" && allowsEntry(item.standing, standing) && companyScopeAllows(item.route, held),
  );
}
